import type { FormConfig } from '@/utils/form';
import { useGlobalStore } from '@/features/Global/store';

import { fahrerkreisCompanyNutzerSection as campKradFahrerkreisCompanyNutzerSection } from '@/features/Sparten/shared/formConfigs/kfz/input/campKrad/fahrerkreisCompanyNutzerFormConfig';
import { fahrerkreisSection as campKradFahrerkreisSection } from '@/features/Sparten/shared/formConfigs/kfz/input/campKrad/fahrerkreisFormConfig';
import { fahrerkreisNutzerSection as campKradFahrerkreisNutzerSection } from '@/features/Sparten/shared/formConfigs/kfz/input/campKrad/fahrerkreisNutzerFormConfig';

import { fahrerkreisCompanyNutzerSection as nutzFahrerkreisCompanyNutzerSection } from '@/features/Sparten/nutz/formConfigs/inputFormConfigs/fahrerkreisCompanyNutzerFormConfig';
import { fahrerkreisSection as nutzFahrerkreisSection } from '@/features/Sparten/nutz/formConfigs/inputFormConfigs/fahrerkreisFormConfig';
import { fahrerkreisNutzerSection as nutzFahrerkreisNutzerSection } from '@/features/Sparten/nutz/formConfigs/inputFormConfigs/fahrerkreisNutzerFormConfig';

import { fahrerkreisCompanyNutzerSection as nutzv2FahrerkreisCompanyNutzerSection } from '@/features/Sparten/nutzv2/formConfigs/inputFormConfigs/fahrerkreisCompanyNutzerFormConfig';
import { fahrerkreisSection as nutzv2FahrerkreisSection } from '@/features/Sparten/nutzv2/formConfigs/inputFormConfigs/fahrerkreisFormConfig';
import { fahrerkreisNutzerSection as nutzv2FahrerkreisNutzerSection } from '@/features/Sparten/nutzv2/formConfigs/inputFormConfigs/fahrerkreisNutzerFormConfig';

import { fahrerkreisCompanyNutzerSection as pkwFahrerkreisCompanyNutzerSection } from '@/features/Sparten/pkw/formConfigs/inputFormConfigs/fahrerkreisCompanyNutzerFormConfig';
import { fahrerkreisSection as pkwFahrerkreisSection } from '@/features/Sparten/pkw/formConfigs/inputFormConfigs/fahrerkreisFormConfig';
import { fahrerkreisNutzerSection as pkwFahrerkreisNutzerSection } from '@/features/Sparten/pkw/formConfigs/inputFormConfigs/fahrerkreisNutzerFormConfig';

import { VglError } from '@/services/errorHandler/vglError';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { isSingleUser } from '@/features/Sparten/shared/schema/kfz/fahrerkreisSchema/fahrerkreisSchema';
import { computed } from 'vue';
import {
  ClientResourceContainer,
  Role,
  useClientStore,
} from '@/features/Clients';
import { getEntries } from '@/utils/objects/objects';
import { KfzFahrerResource } from '@/features/Sparten/shared/schema/kfz/clientSchema/clientSchema';
import { VALIDATION_MAP_KEYS } from '@/features/validation/validationMapKeys';
import { useValidation } from '@/utils/form';

export const getFahrerkreisSections = (): Record<string, FormConfig> => {
  switch (useGlobalStore().sparte) {
    // pkwv2 ist strukturell ein pkw-Klon (wkz-lose KFZ-Sparte) und nutzt die pkw-Fahrerkreis-Sektionen.
    case 'pkw':
    case 'pkwv2':
      return {
        kfzFahrerkreisCompanyNutzerSection: pkwFahrerkreisCompanyNutzerSection,
        kfzFahrerkreisSection: pkwFahrerkreisSection,
        kfzFahrerkreisNutzerSection: pkwFahrerkreisNutzerSection,
      };
    case 'nutz':
      return {
        kfzFahrerkreisCompanyNutzerSection: nutzFahrerkreisCompanyNutzerSection,
        kfzFahrerkreisSection: nutzFahrerkreisSection,
        kfzFahrerkreisNutzerSection: nutzFahrerkreisNutzerSection,
      };
    // Kein Fallthrough auf 'nutz': nutzv2 hat eigene Fahrerkreis-Configs (V1/V2-Isolation).
    case 'nutzv2':
      return {
        kfzFahrerkreisCompanyNutzerSection:
          nutzv2FahrerkreisCompanyNutzerSection,
        kfzFahrerkreisSection: nutzv2FahrerkreisSection,
        kfzFahrerkreisNutzerSection: nutzv2FahrerkreisNutzerSection,
      };
    case 'camp':
    case 'campv2':
    case 'krad':
    case 'kradv2':
      return {
        kfzFahrerkreisCompanyNutzerSection:
          campKradFahrerkreisCompanyNutzerSection,
        kfzFahrerkreisSection: campKradFahrerkreisSection,
        kfzFahrerkreisNutzerSection: campKradFahrerkreisNutzerSection,
      };
    default:
      throw new VglError(VGL_ERROR_CODE.GLOBAL_NO_SPARTE);
  }
};

export const relevantFahrerRoles: Role[] = [
  'fahrer1' as Role, // type casting necessary, otherwise tests would fail to resolve the values
  'fahrer2' as Role,
  'fahrer3' as Role,
  'fahrer4' as Role,
  'fahrer5' as Role,
];

export const availableFahrerRoles = computed(() => {
  const result: Partial<Record<Role, boolean>> = {};

  relevantFahrerRoles.forEach((role) => {
    result[role] = useClientStore().hasClientWithRole(role);
  });

  return result;
});

export const usedFahrerRoles = computed(() => {
  const roles: Role[] = [];

  getEntries(availableFahrerRoles.value).forEach(([role, isInUse]) => {
    if (isInUse === true) {
      roles.push(role);
    }
  });

  return roles;
});

export const nextAvailableFahrerRole = computed(() => {
  let nextRole: Role | undefined;

  getEntries(availableFahrerRoles.value).forEach(([role, isInUse]) => {
    if (isInUse === false && nextRole === undefined) {
      nextRole = role;
    }
  });

  return nextRole;
});

export const driverNameForRole = computed(
  () => (client?: ClientResourceContainer['data']) => {
    const nameParts = [client?.vorname ?? '', client?.nachname ?? ''];

    return nameParts.join(' ').trim() || 'Fahrer';
  },
);

export const getDriverWithHauptnutzerFlag = () => {
  let count = 0;
  usedFahrerRoles.value.forEach((role) => {
    const driver = useClientStore().getClientByRole<KfzFahrerResource>(role);
    if (driver.data.meta.isHauptnutzer) {
      count += 1;
    }
  });

  return count;
};

export const getDriverWithSingleUserFlag = () => {
  let count = 0;
  usedFahrerRoles.value.forEach((role) => {
    const driver = useClientStore().getClientByRole<KfzFahrerResource>(role);
    if (isSingleUser(driver.data.meta.bezugZumVn)) {
      count += 1;
    }
  });

  return count;
};

export const removeDriverByRole = (role: Role) => {
  const clientStore = useClientStore();
  const { removeClientValidatorByRole } = useValidation(
    VALIDATION_MAP_KEYS.INPUT,
  );

  clientStore.removeClientByRole(role, true);
  removeClientValidatorByRole(role);
};
